import React, { useRef, useState } from 'react';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { usePlaylistItemCallback } from '@jwp/ott-hooks-react/src/usePlaylistItemCallback';
import { useAdSchedule } from '@jwp/ott-hooks-react/src/useAdSchedule';
import usePlayableMedia from '@jwp/ott-hooks-react/src/usePlayableMedia';

import Player from '../../components/Player/Player';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import PlayerError, { PlayerErrorState } from '../../components/PlayerError/PlayerError';
import { useWatchHistoryListener } from '../../hooks/useWatchHistoryListener';
import type { EventParams, PlayerHandle } from '../../types/player';

type Props = {
  item: PlaylistItem;
  seriesItem?: PlaylistItem;
  onPlay?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  onClose?: () => void;
  onUserActive?: () => void;
  onUserInActive?: () => void;
  onNext?: () => void;
  onBackClick?: () => void;
  feedId?: string;
  liveStartDateTime?: string | null;
  liveEndDateTime?: string | null;
  liveFromBeginning?: boolean;
  autostart?: boolean;
  trackHistory?: boolean;
};

const PlayerContainer: React.FC<Props> = ({
  item,
  seriesItem,
  feedId,
  onPlay,
  onPause,
  onComplete,
  onUserActive,
  onUserInActive,
  onNext,
  onBackClick,
  liveEndDateTime,
  liveFromBeginning,
  liveStartDateTime,
  autostart,
  trackHistory = true,
}: Props) => {
  // data
  const [playerError, setPlayerError] = useState<Error | undefined>(undefined);
  const { data: adsData, isLoading: isAdsLoading } = useAdSchedule({ mediaId: item?.mediaid });
  const { saveWatchProgress, handleTimeUpdate } = useWatchHistoryListener(trackHistory, item, seriesItem);
  const { playableItem, startOffset, isLoading, error } = usePlayableMedia(item, { trackHistory, onStopPlayback: saveWatchProgress });

  // player events
  const playerHandle = useRef<PlayerHandle>(null);
  const handleFirstFrame = () => {
    // when playing a livestream, the first moment we can seek to the beginning of the DVR range is after the
    // firstFrame event.
    // @todo this doesn't seem to work 100% out of the times. Confirm with player team if this is the best approach.
    if (liveFromBeginning) {
      playerHandle.current?.seek(0);
    }
  };

  const handleTimeEvent = (params: { position: number; duration: number }) => {
    handleTimeUpdate(params);
  };

  const handlePlay = () => {
    saveWatchProgress();
    onPlay?.();
  };

  const handlePause = () => {
    saveWatchProgress();
    onPause?.();
  };

  const handleComplete = () => {
    onComplete?.();
  };

  const handleRemove = () => {
    saveWatchProgress();
  };

  const onError = (event: EventParams<'error' | 'setupError', { error: Error }>) => {
    setPlayerError(event.error);
  };

  const handlePlaylistItemCallback = usePlaylistItemCallback(liveStartDateTime, liveEndDateTime);

  if (isLoading || isAdsLoading) {
    return <LoadingOverlay inline />;
  }

  if (!playableItem || error instanceof Error || playerError) {
    let errorState = PlayerErrorState.UNKNOWN;

    if (error instanceof Error) {
      if (error.message.toLowerCase() === 'access blocked') errorState = PlayerErrorState.GEO_BLOCKED;
      if (error.message.toLowerCase() === 'too many sessions') errorState = PlayerErrorState.TOO_MANY_SESSIONS;
      if (error.message.toLowerCase() === 'unauthorized') errorState = PlayerErrorState.UNAUTHORIZED;
    }

    return <PlayerError error={errorState} />;
  }

  return (
    <Player
      feedId={feedId}
      item={playableItem}
      adsData={adsData}
      on={{
        firstFrame: handleFirstFrame,
        play: handlePlay,
        pause: handlePause,
        time: handleTimeEvent,
        complete: handleComplete,
        userActive: onUserActive,
        userInactive: onUserInActive,
        remove: handleRemove,
        next: onNext,
        backClick: onBackClick,
        error: onError,
        setupError: onError,
      }}
      onPlaylistItemCallback={handlePlaylistItemCallback}
      startTime={startOffset}
      autostart={autostart}
      playerHandle={playerHandle}
    />
  );
};

export default PlayerContainer;
